import { useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { Card } from 'src/shared/components/ui/card'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Pencil,
  Trash2,
} from 'lucide-react'
import {
  getFilenameFromUrl,
  isPdfUrl,
  type ProofEntry,
  type ProofStatus,
} from 'src/shared/utils/proofUrls'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOptionalRequestContext } from '../../../../hooks/RequestContext'
import { cn } from 'src/shared/components/ui/utils'
import { ProofCommentField } from './ProofCommentField'

export const ProofMediaPreview = ({
  url,
  filename,
  className,
  imageClassName,
  iframeClassName,
}: {
  url: string
  filename: string
  className?: string
  imageClassName?: string
  iframeClassName?: string
}) => {
  const { t } = useLocalization()
  const isPdf = isPdfUrl(url)

  return (
    <Column className={cn('group relative', className)}>
      {isPdf ? (
        <iframe
          src={url}
          title={filename}
          className={cn('w-full bg-white', iframeClassName)}
        />
      ) : (
        <img
          src={url}
          alt={filename}
          className={cn('w-full object-contain', imageClassName)}
        />
      )}

      <button
        type="button"
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-black/5 bg-white/90 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover:opacity-100"
        aria-label={t('Open in new tab', 'Öppna i ny flik')}
      >
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </button>
    </Column>
  )
}

export const ProofThumbnail = ({
  url,
  filename,
  className,
}: {
  url: string
  filename: string
  className?: string
}) => {
  const isPdf = isPdfUrl(url)

  return (
    <Column
      className={cn(
        'shrink-0 overflow-hidden rounded-[var(--radius-card-inner)] border border-[hsl(var(--border-inner))] bg-muted',
        className
      )}
    >
      {isPdf ? (
        <Column className="h-full w-full items-center justify-center bg-muted">
          <FileText className="h-5 w-5 text-muted-foreground" />
        </Column>
      ) : (
        <img src={url} alt="" className="h-full w-full object-cover" />
      )}
    </Column>
  )
}

const filmstripStatusLabel = (
  status: ProofStatus,
  t: (en: string, sv?: string) => string
) => {
  switch (status) {
    case 'approved':
      return t('Approved', 'Godkänd')
    case 'not_approved':
      return t('Changes', 'Ändringar')
    default:
      return t('To review', 'Att granska')
  }
}

const filmstripStatusClass = (status: ProofStatus) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-50 text-emerald-700'
    case 'not_approved':
      return 'bg-amber-50 text-amber-800'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

const ProofFilmstripItem = ({
  proof,
  index,
  isSelected,
  onSelect,
  t,
}: {
  proof: ProofEntry
  index: number
  isSelected: boolean
  onSelect: () => void
  t: (en: string, sv?: string) => string
}) => {
  const isPdf = isPdfUrl(proof.url)
  const statusText = filmstripStatusLabel(proof.status, t)

  return (
    <button
      type="button"
      onClick={onSelect}
      className="group shrink-0"
      aria-label={t(
        `Proof ${index + 1}, ${statusText}`,
        `Korrektur ${index + 1}, ${statusText}`
      )}
      aria-current={isSelected ? 'true' : undefined}
    >
      <Column className="items-center gap-1.5">
        <Column
          className={cn(
            'relative h-16 w-24 overflow-hidden rounded-md border bg-[hsl(var(--surface-panel))] transition-colors',
            isSelected
              ? 'border-foreground/25'
              : 'border-transparent opacity-60 group-hover:opacity-100'
          )}
        >
          {isPdf ? (
            <Column className="h-full w-full items-center justify-center">
              <FileText className="h-4 w-4 text-muted-foreground" />
            </Column>
          ) : (
            <img
              src={proof.url}
              alt=""
              className="h-full w-full object-contain p-1"
            />
          )}
        </Column>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] leading-none',
            filmstripStatusClass(proof.status),
            !isSelected && 'opacity-70 group-hover:opacity-100'
          )}
        >
          {statusText}
        </span>
      </Column>
    </button>
  )
}

const ProofDecision = ({
  status,
  isTweaking,
  showApproveDeny,
  canEditComment,
  showComment,
  commentValue,
  proofId,
  onApprove,
  onStartTweak,
  onSendChanges,
  onCommentChange,
  t,
}: {
  status: ProofStatus
  isTweaking: boolean
  showApproveDeny: boolean
  canEditComment: boolean
  showComment: boolean
  commentValue: string
  proofId: string
  onApprove: () => void
  onStartTweak: () => void
  onSendChanges: () => void
  onCommentChange: (comment: string) => void
  t: (en: string, sv?: string) => string
}) => {
  if (status === 'approved') {
    return (
      <Row className="items-center justify-center gap-2 py-2 text-sm text-emerald-700">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        {t('Looks good', 'Ser bra ut')}
      </Row>
    )
  }

  if (status === 'not_approved') {
    return (
      <Column className="items-center gap-3 py-1">
        <span className="text-sm text-muted-foreground">
          {t('Notes sent — we will take another pass.', 'Anteckningar skickade — vi tar ett varv till.')}
        </span>
        {showComment ? (
          <Column className="w-full max-w-md rounded-2xl bg-[hsl(var(--surface-panel))] px-4 py-3">
            <ProofCommentField
              proofId={proofId}
              value={commentValue}
              readOnly
              hideLabel
              onSave={() => undefined}
            />
          </Column>
        ) : null}
      </Column>
    )
  }

  if (!showApproveDeny && !showComment) return null

  return (
    <Column className="items-center gap-4">
      {showApproveDeny ? (
        <Column className="items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {t('Does this look right?', 'Ser det här rätt ut?')}
          </span>
          <Row className="flex-wrap items-center justify-center gap-2">
            <Button variant="green" size="md" onClick={onApprove}>
              <Check className="mr-1.5 h-4 w-4" />
              {t('Looks great', 'Ser bra ut')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={onStartTweak}
              className={cn(isTweaking && 'border-foreground/20 bg-muted')}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {t('A small tweak', 'En liten justering')}
            </Button>
          </Row>
        </Column>
      ) : null}

      <AnimatePresence initial={false}>
        {isTweaking && canEditComment ? (
          <motion.div
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 4, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full max-w-lg overflow-hidden"
          >
            <Column className="gap-3 rounded-2xl bg-[hsl(var(--surface-panel))] px-4 py-4">
              <span className="text-sm text-foreground">
                {t('What should we change?', 'Vad ska vi ändra?')}
              </span>
              <ProofCommentField
                proofId={proofId}
                value={commentValue}
                hideLabel
                showSuggestions
                autoFocus
                onSave={onCommentChange}
              />
              <Row className="justify-end">
                <Button variant="secondary" size="sm" onClick={onSendChanges}>
                  {t('Send notes', 'Skicka anteckningar')}
                </Button>
              </Row>
            </Column>
          </motion.div>
        ) : showComment && !canEditComment ? (
          <ProofCommentField
            proofId={proofId}
            value={commentValue}
            readOnly
            onSave={() => undefined}
          />
        ) : null}
      </AnimatePresence>
    </Column>
  )
}

const ProofReviewPanel = ({
  proof,
  index,
  total,
  readOnly,
  canRemove,
  canSetStatus,
  showCommentField,
  commentValue,
  onCommentChange,
  onRemoveClick,
  onStatusChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  filmstrip,
  t,
}: {
  proof: ProofEntry
  index: number
  total: number
  readOnly: boolean
  canRemove: boolean
  canSetStatus: boolean
  showCommentField: boolean
  commentValue: string
  onCommentChange: (comment: string) => void
  onRemoveClick?: (url: string) => void
  onStatusChange?: (url: string, status: ProofStatus) => void
  onPrevious: () => void
  onNext: () => void
  hasPrevious: boolean
  hasNext: boolean
  filmstrip?: ReactNode
  t: (en: string, sv?: string) => string
}) => {
  const { url, status } = proof
  const filename = getFilenameFromUrl(url)
  const showApproveDeny = canSetStatus && status === 'pending'
  const canEditComment =
    !readOnly && showCommentField && status === 'pending'
  const showComment = canEditComment || Boolean(commentValue.trim())

  const [isTweaking, setIsTweaking] = useState(
    () => Boolean(commentValue.trim()) && status === 'pending'
  )

  useEffect(() => {
    setIsTweaking(Boolean((proof.customer_comment ?? '').trim()) && proof.status === 'pending')
  }, [proof.proof_id, proof.url])

  const handleSendChanges = () => {
    if (!commentValue.trim() && !isTweaking) {
      setIsTweaking(true)
      return
    }
    onStatusChange?.(url, 'not_approved')
  }

  return (
    <Column className="min-w-0 flex-1 gap-5">
      <Column className="relative overflow-hidden rounded-[1.25rem] bg-[hsl(var(--surface-panel))]">
        {canRemove && onRemoveClick ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemoveClick(url)}
            className="absolute left-3 top-3 z-10 text-muted-foreground hover:text-destructive"
            aria-label={t('Remove', 'Ta bort')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}

        {total > 1 ? (
          <>
            <button
              type="button"
              disabled={!hasPrevious}
              onClick={onPrevious}
              className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-black/5 bg-white/90 text-foreground shadow-sm transition-opacity disabled:opacity-0"
              aria-label={t('Previous proof', 'Föregående korrektur')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={!hasNext}
              onClick={onNext}
              className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-black/5 bg-white/90 text-foreground shadow-sm transition-opacity disabled:opacity-0"
              aria-label={t('Next proof', 'Nästa korrektur')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        ) : null}

        <AnimatePresence mode="wait">
          <motion.div
            key={proof.proof_id ?? proof.url}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ProofMediaPreview
              url={url}
              filename={filename}
              className="min-h-[280px]"
              iframeClassName="h-[min(52vh,560px)] w-full bg-white"
              imageClassName="mx-auto max-h-[min(52vh,560px)] w-full object-contain p-8"
            />
          </motion.div>
        </AnimatePresence>
      </Column>

      {filmstrip}

      <ProofDecision
        status={status}
        isTweaking={isTweaking}
        showApproveDeny={showApproveDeny}
        canEditComment={canEditComment}
        showComment={showComment}
        commentValue={commentValue}
        proofId={proof.proof_id ?? `proof-${index}`}
        onApprove={() => onStatusChange?.(url, 'approved')}
        onStartTweak={() => setIsTweaking((open) => !open)}
        onSendChanges={handleSendChanges}
        onCommentChange={onCommentChange}
        t={t}
      />
    </Column>
  )
}

export const ProofReviewWorkspace = ({
  proofs,
  readOnly,
  isShareOrder,
  quoteId,
  onRemoveClick,
  onStatusChange,
  onCommentChange,
  showStatus,
  toolbar,
  embedded = false,
  t,
}: {
  proofs: ProofEntry[]
  readOnly: boolean
  isShareOrder?: boolean
  quoteId?: number
  onRemoveClick?: (url: string) => void
  onStatusChange?: (url: string, status: ProofStatus) => void
  onCommentChange?: (proofId: string, comment: string) => void
  showStatus: boolean
  toolbar?: ReactNode
  /** Skip outer Card + title when already inside a parent card. */
  embedded?: boolean
  t: (en: string, sv?: string) => string
  locale: string
}) => {
  const isAdmin = useIsAdmin()
  const { isDemo } = useOptionalRequestContext() ?? {}
  const canRemove = Boolean(
    isAdmin && !readOnly && !isShareOrder && quoteId && onRemoveClick
  )
  const canSetStatus = !readOnly && onStatusChange != null
  const showCommentField = !readOnly && onCommentChange != null

  const [selectedIndex, setSelectedIndex] = useState(() => {
    const pendingIndex = proofs.findIndex((p) => p.status === 'pending')
    return pendingIndex >= 0 ? pendingIndex : 0
  })
  const [demoStatusByUrl, setDemoStatusByUrl] = useState<
    Record<string, ProofStatus>
  >({})

  useEffect(() => {
    setSelectedIndex((index) =>
      proofs.length === 0 ? 0 : Math.min(index, proofs.length - 1)
    )
  }, [proofs.length])

  const visibleProofs = proofs.map((proof) => ({
    ...proof,
    status: demoStatusByUrl[proof.url] ?? proof.status,
  }))

  const safeIndex = Math.min(selectedIndex, Math.max(0, visibleProofs.length - 1))
  const selectedProof = visibleProofs[safeIndex]

  const getCommentValue = (proof: ProofEntry) => proof.customer_comment ?? ''

  const handleCommentChange = (proof: ProofEntry, comment: string) => {
    if (!proof.proof_id) return
    onCommentChange?.(proof.proof_id, comment)
  }

  const handleStatusChange = (url: string, status: ProofStatus) => {
    if (isDemo) {
      const nextStatus = { ...demoStatusByUrl, [url]: status }
      setDemoStatusByUrl(nextStatus)
      const allApproved = proofs.every(
        (proof) => (nextStatus[proof.url] ?? proof.status) === 'approved'
      )
      if (allApproved && status === 'approved') {
        onStatusChange?.(url, status)
      }
    } else {
      onStatusChange?.(url, status)
    }

    const nextIndex = safeIndex + 1
    if (nextIndex < visibleProofs.length) {
      setSelectedIndex(nextIndex)
    }
  }

  if (!selectedProof) return null

  const content = (
    <Column className="gap-5">
      <ProofReviewPanel
        proof={selectedProof}
        index={safeIndex}
        total={visibleProofs.length}
        readOnly={readOnly}
        canRemove={canRemove}
        canSetStatus={canSetStatus}
        showCommentField={showCommentField}
        commentValue={getCommentValue(selectedProof)}
        onCommentChange={(comment) =>
          handleCommentChange(selectedProof, comment)
        }
        onRemoveClick={onRemoveClick}
        onStatusChange={handleStatusChange}
        onPrevious={() => setSelectedIndex((i) => Math.max(0, i - 1))}
        onNext={() =>
          setSelectedIndex((i) => Math.min(visibleProofs.length - 1, i + 1))
        }
        hasPrevious={safeIndex > 0}
        hasNext={safeIndex < visibleProofs.length - 1}
        filmstrip={
          visibleProofs.length > 1 ? (
            <Row className="justify-center gap-3 overflow-x-auto px-1">
              {visibleProofs.map((proof, index) => (
                <ProofFilmstripItem
                  key={proof.proof_id ?? `${proof.url}-${index}`}
                  proof={proof}
                  index={index}
                  isSelected={index === safeIndex}
                  onSelect={() => setSelectedIndex(index)}
                  t={t}
                />
              ))}
            </Row>
          ) : undefined
        }
        t={t}
      />
    </Column>
  )

  if (embedded) {
    return (
      <Column className="gap-4">
        {toolbar ? <Row className="justify-end">{toolbar}</Row> : null}
        {content}
      </Column>
    )
  }

  return (
    <Column className="px-4 pb-24">
      <Card className="overflow-hidden p-0">
        {showStatus ? (
          <Row className="w-full items-center justify-between gap-4 px-5 py-4">
            <h2>{t('Review proofs', 'Granska korrektur')}</h2>
            {toolbar ? <Row className="shrink-0">{toolbar}</Row> : null}
          </Row>
        ) : toolbar ? (
          <Row className="justify-end px-5 py-3">{toolbar}</Row>
        ) : null}
        <Column
          className={
            showStatus || toolbar
              ? 'border-t border-[hsl(var(--border-inner))] px-5 py-5'
              : 'px-5 py-5'
          }
        >
          {content}
        </Column>
      </Card>
    </Column>
  )
}
